import React, { useState } from 'react';
import { Modal, Typography, Button, Input, Form, Alert, AlertDescription, useToast } from '@crisellevaldez/speculo-ui';
import { Organization } from '../../../types/organization.types';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAddCRMIntegration } from '../../../hooks/integrations/crm/useAddCRMIntegration';
import { Save, Code } from 'lucide-react';
import { CodeBlock } from '../../../components/common/CodeBlock';
import WebsiteIntegrationModal from './WebsiteIntegrationModal';

interface CRMModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
  onSuccess?: () => void;
}

// Form schema for CRM integration
const crmFormSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

type CRMFormData = z.infer<typeof crmFormSchema>;

export const CRMModal: React.FC<CRMModalProps> = ({ isOpen, onClose, selectedOrg, onSuccess }) => {
  const { showToast } = useToast();
  const { addCRMIntegration, isPending } = useAddCRMIntegration();
  const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false);

  // Function to check if CRM is configured
  const isCRMConfigured = () => {
    return Boolean(selectedOrg?.externalClient);
  };

  const form = useForm<CRMFormData>({
    resolver: zodResolver(crmFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Clear form when modal is closed
  const handleClose = () => {
    form.reset({
      email: '',
      password: '',
    });
    onClose();
  };

  const handleSubmit = async (data: CRMFormData) => {
    if (!selectedOrg) return;

    try {
      // Call the hook function to add CRM integration
      const success = await addCRMIntegration({
        organizationId: selectedOrg.id,
        email: data.email,
        password: data.password,
      });

      if (success) {
        showToast({
          message: 'CRM integration configured successfully',
          variant: 'success',
        });

        setIsIntegrationModalOpen(true);

        // Call onSuccess callback to refresh the parent component
        onSuccess?.();
      }
    } catch (error) {
      console.error('Error configuring CRM integration:', error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>
        <Typography variant="h3">CRM</Typography>
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          {/* Always show the form for updating credentials */}
          <p className="text-gray-600 dark:text-gray-400">Connect your account to sync contacts and activities.</p>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4" noValidate>
            <Form.Control required>
              <Form.Label>Email</Form.Label>
              <Input
                {...form.register('email')}
                type="text"
                placeholder="Enter your client email"
                error={form.formState.errors.email?.message}
              />
              <Form.HelperText>Please enter the email address for your CRM account</Form.HelperText>
            </Form.Control>

            <Form.Control required>
              <Form.Label>Password</Form.Label>
              <Input
                {...form.register('password')}
                type="password"
                placeholder="Enter your client password"
                error={form.formState.errors.password?.message}
              />
              <Form.HelperText>Enter the password for your CRM account</Form.HelperText>
            </Form.Control>
          </form>

          {/* Show success message with integration link when configured */}
          {isCRMConfigured() && (
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-white/10">
              <Alert variant="success" className="mb-4">
                <AlertDescription>
                  <div className="flex flex-col">
                    <p>CRM Integration Configured Successfully!</p>
                    <button
                      className="text-left text-blue-600 hover:underline flex items-center gap-2 mt-1 text-sm font-medium"
                      onClick={() => setIsIntegrationModalOpen(true)}
                    >
                      <Code className="w-4 h-4" />
                      View Website Integration Instructions
                    </button>
                  </div>
                </AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline" onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={form.handleSubmit(handleSubmit)}
          disabled={isPending}
          isLoading={isPending}
          className="flex items-center gap-1 h-8"
        >
          <Save className="h-4 w-4" />
          Save Configuration
        </Button>
      </Modal.Footer>

      {/* Website Integration Instructions Modal */}
      <WebsiteIntegrationModal
        isOpen={isIntegrationModalOpen}
        onClose={() => setIsIntegrationModalOpen(false)}
        orgId={selectedOrg?.id}
      />
    </Modal>
  );
};
